/**
 * UI-3c Task 8 — CallbacksPage.
 *
 * Tenant-admin scheduled-callbacks list. Composition is plan §0.F (authoritative):
 *
 *   - The list is fetched with `useAdminResource('/callbacks', { responseKey:
 *     'callbacks' })`. The Status filter is passed through to the backend via
 *     the hook's `query` option — the hook re-fetches whenever the serialised
 *     query changes, so a status change is a SERVER filter. The page never uses
 *     the hook's `update`/`remove` (the drawer + the Cancel confirmDialog issue
 *     their own `api.patch` calls), so the hook's known query-string-basePath
 *     limitation does not apply here.
 *   - Agent + date-range filters are CLIENT-SIDE (spec §3.3 / §0.F): a `useMemo`
 *     narrows the fetched list before it reaches the table.
 *   - Create / Edit go through `CallbackDrawer` (the drawer owns the save API
 *     call and reports back via `onSaved`).
 *   - Edit is offered ONLY for `PENDING` callbacks (spec §3.4).
 *   - A per-row "Cancel" action (offered for non-terminal PENDING/ORIGINATING
 *     rows) opens a `confirmDialog`; accepting PATCHes `{ status: 'CANCELLED' }`
 *     and refetches (spec §3.5).
 *
 * Backend contract (apps/api/src/routes/callbacks/list.ts):
 *   GET /callbacks?status= -> { callbacks, total, limit, offset }
 *   PATCH /callbacks/:id { status: 'CANCELLED' }
 *
 * Per-row actions: ResourceTable's built-in `actions` prop only knows
 * `edit`/`delete`, and it renders Edit for EVERY row — but spec §3.4 wants Edit
 * only for PENDING rows, and the Cancel action is neither edit nor delete. So
 * the table is given `actions={[]}` (no built-in action buttons) and BOTH Edit
 * and Cancel are rendered together in a render-function "Actions" column, each
 * gated to the statuses where it is valid (Edit → PENDING; Cancel → PENDING /
 * ORIGINATING). This keeps the shared ResourceTable untouched.
 */
import { useMemo, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Tag } from 'primereact/tag'
import { Calendar } from 'primereact/calendar'
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog'

import PageHeader from '../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../components/admin/ResourceTable.js'
import RequirePermission from '../../components/auth/RequirePermission.js'
import { CallbackDrawer, type Callback } from '../../components/admin/callbacks/CallbackDrawer.js'
import { useAdminResource } from '../../hooks/useAdminApi.js'
import { useAgents } from '../../hooks/useAgents.js'
import { api } from '../../api/client.js'
import { useToast } from '../../components/system/ToastProvider.js'
import { callbackStatusSeverity } from '../../utils/callback-status-severity.js'
import { formatDateTime } from '../../utils/formatDate.js'
import { timestampMs } from '../../utils/timezone.js'
import Select from '../../components/ui/Select.js'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STATUS_OPTIONS: Callback['status'][] = [
  'PENDING',
  'ORIGINATING',
  'COMPLETED',
  'NO_ANSWER',
  'FAILED',
  'CANCELLED',
]

/** Statuses for which a Cancel action still makes sense (not yet terminal). */
const CANCELLABLE: ReadonlySet<Callback['status']> = new Set(['PENDING', 'ORIGINATING'])

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function CallbacksPage(): JSX.Element {
  const toast = useToast()
  const { agents } = useAgents()

  // --- Filters -------------------------------------------------------------
  // Status: SERVER filter — passed through the hook's `query` so the hook
  // refetches when it changes. Agent + date range: CLIENT-SIDE.
  const [statusFilter, setStatusFilter] = useState<Callback['status'] | ''>('')
  const [agentFilter, setAgentFilter] = useState<string>('')
  // PrimeReact's range Calendar emits `[Date, null]` after the user's FIRST
  // click (before the second date is picked) — so the second end is honestly
  // nullable. A one-ended (or null) range means "no date filter".
  const [dateRange, setDateRange] = useState<[Date, Date | null] | null>(null)

  const {
    items: callbacks,
    loading,
    refetch,
    pagination,
  } = useAdminResource<Callback>('/callbacks', {
    responseKey: 'callbacks',
    query: statusFilter ? { status: statusFilter } : {},
  })

  // --- Client-side filtering ----------------------------------------------
  const filtered = useMemo(() => {
    let rows = callbacks
    if (agentFilter) {
      rows = rows.filter((c) => c.agentId === agentFilter)
    }
    // Only apply the date filter once BOTH ends are present — a partially
    // selected (one-ended) or null range means "no date filter".
    if (dateRange && dateRange[0] && dateRange[1]) {
      const from = dateRange[0]
      const to = dateRange[1]
      rows = rows.filter((c) => {
        const t = timestampMs(c.scheduledAt)
        return t >= from.getTime() && t <= to.getTime()
      })
    }
    return rows
  }, [callbacks, agentFilter, dateRange])

  // --- Drawer state --------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Callback | null>(null)

  // --- Agent label lookup --------------------------------------------------
  function agentLabel(agentId: string | null): string {
    if (!agentId) return 'No preference'
    const a = agents.find((x) => x.id === agentId)
    return a ? `Ext. ${a.extension}` : 'Unknown agent'
  }

  // --- Cancel flow ---------------------------------------------------------
  function handleCancel(c: Callback): void {
    confirmDialog({
      message: `Cancel the callback for ${c.phoneE164}? It will no longer be attempted.`,
      header: 'Cancel Callback',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      // The whole body is try/catch-wrapped so a rejecting `api.patch` can
      // never escape as an unhandled rejection — failures surface as a toast.
      accept: async () => {
        try {
          await api.patch(`/callbacks/${c.id}`, { status: 'CANCELLED' })
          toast.success('Callback cancelled')
          refetch()
        } catch (e) {
          toast.error(e instanceof Error ? e.message : 'Failed to cancel callback')
        }
      },
    })
  }

  // --- Columns -------------------------------------------------------------
  const columns: ColumnDef<Callback>[] = [
    { label: 'Phone', accessor: 'phoneE164' },
    { label: 'Scheduled', accessor: (r) => formatDateTime(r.scheduledAt) },
    { label: 'Agent', accessor: (r) => agentLabel(r.agentId) },
    {
      label: 'Status',
      accessor: (r) => <Tag value={r.status} severity={callbackStatusSeverity(r.status)} />,
    },
    { label: 'Attempts', accessor: (r) => `${r.attemptCount} / ${r.maxAttempts}` },
    {
      // Edit (PENDING only) + Cancel (non-terminal only) live together here —
      // see the file header for why the built-in ResourceTable actions aren't
      // used. The accessor always returns a <span>; per-button visibility is
      // status-gated, so a row with neither button renders an empty <span>.
      label: 'Actions',
      accessor: (r) => (
        <span className="inline-flex gap-2">
          {r.status === 'PENDING' && (
            <Button
              type="button"
              size="small"
              label="Edit"
              text
              severity="secondary"
              aria-label="Edit"
              onClick={() => {
                setEditing(r)
                setDrawerOpen(true)
              }}
            />
          )}
          {CANCELLABLE.has(r.status) && (
            <Button
              type="button"
              size="small"
              label="Cancel"
              severity="danger"
              outlined
              aria-label="Cancel"
              onClick={() => {
                handleCancel(r)
              }}
            />
          )}
        </span>
      ),
    },
  ]

  return (
    <RequirePermission permission="admin:calls:view">
      <div className="space-y-4 p-4">
        <PageHeader
          title="Callbacks"
          description="Scheduled customer callbacks."
          actions={
            <Button
              label="+ New Callback"
              onClick={() => {
                setEditing(null)
                setDrawerOpen(true)
              }}
            />
          }
        />

        {/* Filters: Status (server) + Agent (client) + Date range (client). */}
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Status</span>
                        <Select
              ariaLabel="Filter by status"
              size="sm"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as Callback['status'] | '')}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
                        </Select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Agent</span>
                        <Select
              ariaLabel="Filter by agent"
              size="sm"
              value={agentFilter}
              onChange={(v) => setAgentFilter(v)}
            >
              <option value="">All agents</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  Ext. {a.extension}
                </option>
              ))}
                        </Select>
          </label>

          <label className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Date range</span>
            <Calendar
              aria-label="Filter by scheduled date range"
              value={dateRange}
              onChange={(e) =>
                setDateRange((e.value as [Date, Date | null] | null) ?? null)
              }
              selectionMode="range"
              showIcon
              readOnlyInput
              placeholder="Any date"
            />
          </label>
        </div>

        <ResourceTable<Callback>
          columns={columns}
          rows={filtered}
          loading={loading}
          // No built-in action buttons — Edit + Cancel are in the "Actions"
          // render column above (status-gated). `onEdit` is required by the
          // prop type but is never invoked since `actions` is empty.
          actions={[]}
          onEdit={() => {}}
          empty={{
            title: 'No callbacks',
            description: 'Scheduled customer callbacks will appear here.',
            icon: 'pi pi-phone',
          }}
          pagination={pagination}
        />

        <CallbackDrawer
          open={drawerOpen}
          callback={editing}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            refetch()
            setDrawerOpen(false)
          }}
        />

        <ConfirmDialog />
      </div>
    </RequirePermission>
  )
}
